import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, resolvePhotoUrl } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { trackProfileView } from "@/lib/visitor";
import {
  ShieldCheck, MapPin, Globe, ArrowLeft, Calendar, Send, Facebook, Instagram,
} from "@/lib/icons";
import IdCard from "@/components/IdCard";
import ShareBar, { memberShareTitle } from "@/components/ShareBar";
import { Avatar } from "@/components/PhotoUpload";
import { useSeo } from "@/lib/seo";
import { CategoryChips, selectedCategories } from "@/components/CategoryMultiSelect";

export default function MemberProfile() {
  const { memberId } = useParams();
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [err, setErr] = useState(null);

  useSeo({
    title: m ? memberShareTitle(m.full_name) : undefined,
    description: m
      ? `${m.full_name} is a ${m.category || "photographer"} with a PRONET Pro ID${m.city ? ` based in ${m.city}${m.state ? `, ${m.state}` : ""}` : ""}. View profile and get in touch.`
      : undefined,
    image: m?.photo_url ? resolvePhotoUrl(m.photo_url) : undefined,
    noindex: !!err,
  });

  useEffect(() => {
    api.get(`/public/members/${encodeURIComponent(memberId)}`)
      .then((r) => {
        setM(r.data);
        trackProfileView(memberId).catch(() => {});
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Not found"));
  }, [memberId]);

  if (err) {
    return (
      <div className="container-tight py-24 text-center">
        <div className="font-display text-5xl text-white">Member not found</div>
        <p className="mt-3 text-white/60">{String(err)}</p>
        <Link to="/gomore" className="mt-6 inline-flex items-center gap-2 text-fuchsia-300 font-semibold">
          <ArrowLeft size={16} /> Back to GoMore
        </Link>
      </div>
    );
  }
  if (!m) return <div className="container-tight py-20 text-white/50">Loading member…</div>;

  // Email + phone are hidden from public profile view.
  // Telegram is a username handle (not a phone number) so it remains available.
  const tgHref = m.telegram_username
    ? `https://t.me/${m.telegram_username.replace(/^@/, "")}`
    : null;

  // Owner or admin sees ID-card action buttons; anonymous viewers do not.
  const isOwner = user && user.role === "member" && user.member_id === m.member_id;
  const isAdmin = user && user.role === "admin";
  const canManageIdCard = isOwner || isAdmin;
  // Points at the server-rendered share shim (real Open Graph tags + an
  // instant redirect), not the SPA route directly — WhatsApp/Facebook/
  // Twitter's preview crawlers never run client JS, so sharing the SPA URL
  // itself would show no image and generic text for every member.
  const profileShareUrl = `${window.location.origin}/api/public/members/${encodeURIComponent(m.member_id)}/share`;

  return (
    <div className="relative container-wide py-12">
      <div className="blobs-layer">
        <div className="blob blob-violet w-[500px] h-[500px] -top-40 -left-20 opacity-30" />
        <div className="blob blob-magenta w-[400px] h-[400px] top-40 right-0 opacity-25" style={{ animationDelay: "-9s" }} />
      </div>

      <div className="relative z-10">
        <Link to="/gomore" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-fuchsia-300" data-testid="back-to-directory">
          <ArrowLeft size={14} /> Back to GoMore
        </Link>

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left */}
          <div className="lg:col-span-4 space-y-5">
            <div className="glass rounded-3xl overflow-hidden fade-up">
              <div className="aspect-square relative">
                <Avatar
                  src={m.photo_url}
                  name={m.full_name}
                  avatarKey={m.avatar_key}
                  size="100%"
                  shape="rounded"
                  bordered={false}
                  style={{ fontSize: 80 }}
                />
                <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full glass px-3 py-2 text-xs font-semibold text-emerald-300" title="PRONET Pro ID">
                  <ShieldCheck size={14} /> Pro ID
                </div>
              </div>
              <div className="p-6">
                <div className="overline text-fuchsia-300/70 text-[10px]">{m.member_id}</div>
                <h1 className="mt-2 font-display text-4xl text-white leading-tight">{m.full_name}</h1>
                {m.studio_name && <div className="text-white/60 mt-1">{m.studio_name}</div>}
                <CategoryChips categories={selectedCategories(m)} primary={m.category} className="mt-4" />
              </div>
            </div>

            {tgHref && (
              <div className="glass rounded-3xl p-6 fade-up-slow" data-testid="contact-buttons">
                <div className="overline text-white/50 mb-4">Get in touch</div>
                <div className="space-y-2">
                  <a href={tgHref} target="_blank" rel="noreferrer" data-testid="contact-telegram"
                    className="btn-glow flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold text-white"
                    style={{ background: "linear-gradient(135deg,#0284c7,#0ea5e9)" }}>
                    <Send size={16} /> Telegram
                    {m.telegram_username && <span className="ml-auto text-xs opacity-80">{m.telegram_username}</span>}
                  </a>
                </div>
              </div>
            )}
          </div>

          {/* Right */}
          <div className="lg:col-span-8 space-y-6">
            <div className="glass rounded-3xl p-8 fade-up" data-testid="member-details">
              <div className="overline text-white/50 mb-4">Details</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 text-sm">
                <Field icon={Globe} label="Website" value={m.website} link />
                <Field icon={Facebook} label="Facebook" value={m.facebook_url} link />
                <Field icon={Instagram} label="Instagram" value={m.instagram_url} link />
                <Field icon={MapPin} label="Location" value={[m.area, m.city, m.district, m.state].filter(Boolean).join(", ")} />
                <Field label="Experience" value={m.experience_years ? `${m.experience_years} years` : null} />
                <Field icon={Calendar} label="Validity" value={m.validity_until ? new Date(m.validity_until).toLocaleDateString() : null} />
              </div>
              {m.bio && (
                <div className="mt-8 pt-8 border-t border-white/10">
                  <div className="overline text-white/50 mb-3">About</div>
                  <p className="text-sm text-white/75 leading-relaxed">{m.bio}</p>
                </div>
              )}
            </div>

            <div className="glass rounded-3xl p-6 md:p-8 relative overflow-hidden" data-testid="profile-id-card-section">
              <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-gradient-to-br from-violet-500/30 to-magenta-500/30 blur-2xl" />
              <div className="relative">
                <div className="overline text-fuchsia-300">PRONET Digital ID</div>
                <div className="mt-2 font-display text-2xl md:text-3xl text-white">Pro ID with QR verification</div>
                <p className="mt-2 text-sm text-white/60 max-w-xl">
                  {canManageIdCard
                    ? "Every approved member gets a shareable, tamper-proof PRONET ID card. Scan the QR to verify online."
                    : "Scan the QR code to verify this member on PRONET."}
                </p>
                <div className="mt-6">
                  <IdCard member={m} showActions={canManageIdCard} />
                </div>
              </div>
            </div>

            {/* SHARE — visible to everyone (owner, admin, anonymous viewer) */}
            <ShareBar
              url={profileShareUrl}
              title={memberShareTitle(m.full_name)}
              subtitle={m.studio_name || m.category || ""}
              testidPrefix="profile-share"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, label, value, link }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      {Icon && <Icon size={16} className="text-fuchsia-300 mt-0.5 shrink-0" />}
      <div>
        <div className="overline text-white/40 text-[10px]">{label}</div>
        {link ? (
          <a href={value.startsWith("http") ? value : `https://${value}`} target="_blank" rel="noreferrer"
             className="text-white font-medium hover:text-fuchsia-300 break-all">{value}</a>
        ) : (
          <div className="text-white font-medium break-words">{value}</div>
        )}
      </div>
    </div>
  );
}
